import { useEffect, useState } from 'react';
import { Plus, Trash2, Check, Clock, AlertCircle, Pause, Link2, X } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../api/client';
import { Badge } from '../ui/Badge';
import { Avatar } from '../ui/Avatar';
import { parseDate } from '../../lib/dates';

const STATUSES = [
  { key: 'pendente', label: 'Pendente', color: 'default', icon: Clock },
  { key: 'em_andamento', label: 'Em andamento', color: 'info', icon: AlertCircle },
  { key: 'concluida', label: 'Concluída', color: 'success', icon: Check },
  { key: 'bloqueada', label: 'Bloqueada', color: 'danger', icon: Pause },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

export function TarefasSection({ demanda, podeEditar }) {
  const [lista, setLista] = useState([]);
  const [adding, setAdding] = useState(false);
  const [responsaveis, setResponsaveis] = useState([]);
  // Tarefa que está em fluxo de "Concluir" — exibe o form de entrega
  const [concluindoId, setConcluindoId] = useState(null);
  const [erroId, setErroId] = useState(null);

  const load = () => api.get(`/demandas/${demanda.codigo}/tarefas`).then((r) => setLista(r.tarefas));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [demanda.codigo]);

  useEffect(() => {
    if (podeEditar) {
      api.get('/usuarios').then((r) => setResponsaveis(r.usuarios.filter((u) => u.perfil !== 'secretario' && u.ativo))).catch(() => {});
    }
  }, [podeEditar]);

  // Mudança de status — se for pra "concluida" e ainda não tem entrega
  // registrada, abre o form em vez de mandar direto pro backend.
  const mudarStatus = async (tarefa, status) => {
    setErroId(null);
    if (status === 'concluida' && tarefa.status !== 'concluida' && !tarefa.entrega_url) {
      setConcluindoId(tarefa.id);
      return;
    }
    try {
      await api.patch(`/demandas/${demanda.codigo}/tarefas/${tarefa.id}`, { status });
      load();
    } catch (e) {
      if (e.data?.requer_entrega) {
        setConcluindoId(tarefa.id);
      } else {
        setErroId({ id: tarefa.id, msg: e.message });
      }
    }
  };

  const concluirComEntrega = async (tarefa, entrega_url, entrega_observacao) => {
    await api.patch(`/demandas/${demanda.codigo}/tarefas/${tarefa.id}`, {
      status: 'concluida',
      entrega_url,
      entrega_observacao: entrega_observacao || null,
    });
    setConcluindoId(null);
    load();
  };
  const mudarResponsavel = async (tarefa, responsavel_id) => {
    await api.patch(`/demandas/${demanda.codigo}/tarefas/${tarefa.id}`, { responsavel_id: responsavel_id ? parseInt(responsavel_id, 10) : null });
    load();
  };
  const remover = async (tarefa) => {
    if (!confirm('Remover esta tarefa?')) return;
    await api.del(`/demandas/${demanda.codigo}/tarefas/${tarefa.id}`);
    load();
  };

  const pendentes = lista.filter((t) => t.status !== 'concluida').length;
  const total = lista.length;

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong>Tarefas da produção</strong>
          <div className="text-xs muted">
            {total === 0 ? 'Divida a produção em tarefas e atribua responsáveis.'
              : `${total - pendentes}/${total} concluídas`}
          </div>
        </div>
        {podeEditar && !adding && (
          <button className="btn btn-secondary btn-sm" onClick={() => setAdding(true)}>
            <Plus size={14} /> Nova tarefa
          </button>
        )}
      </div>
      <div className="card-body">
        {adding && (
          <AddForm
            demandaId={demanda.codigo}
            responsaveis={responsaveis}
            onCancel={() => setAdding(false)}
            onCreated={() => { setAdding(false); load(); }}
          />
        )}

        {lista.length === 0 ? (
          <div className="text-sm muted">Nenhuma tarefa ainda.</div>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {lista.map((t) => {
              const s = STATUS_MAP[t.status];
              const Icon = s.icon;
              const emConclusao = concluindoId === t.id;
              return (
                <div key={t.id} style={{ padding: 10, border: '1px solid var(--c-border)', borderRadius: 8, background: t.status === 'concluida' ? 'var(--c-success-soft)' : 'white' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <button onClick={() => mudarStatus(t, t.status === 'concluida' ? 'pendente' : 'concluida')} disabled={!podeEditar} style={{ border: 'none', background: 'transparent', cursor: podeEditar ? 'pointer' : 'default', marginTop: 2 }}>
                      {t.status === 'concluida' ? <Check size={18} color="var(--c-success)" /> : <div style={{ width: 18, height: 18, borderRadius: 9, border: '2px solid var(--c-border-strong)' }} />}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, textDecoration: t.status === 'concluida' ? 'line-through' : 'none' }}>
                        {t.titulo}
                      </div>
                      {t.descricao && <div className="text-xs muted" style={{ marginTop: 2 }}>{t.descricao}</div>}
                      <div className="row-wrap" style={{ gap: 8, marginTop: 6, fontSize: 11 }}>
                        <Badge color={s.color}><Icon size={10} /> {s.label}</Badge>
                        {t.responsavel_nome && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--c-text-muted)' }}>
                            <Avatar nome={t.responsavel_nome} src={t.responsavel_foto} size="sm" />
                            {t.responsavel_nome?.split(' ')[0]}
                          </span>
                        )}
                        {!t.responsavel_nome && <span className="text-xs muted">sem responsável</span>}
                        {t.prazo && <span style={{ color: 'var(--c-warning)' }}>⏰ {format(parseDate(t.prazo), 'dd/MM')}</span>}
                      </div>
                      {/* Link da entrega — sempre visível quando registrado */}
                      {t.entrega_url && (
                        <div style={{ marginTop: 8, padding: '6px 10px', background: 'white', border: '1px solid var(--c-border)', borderRadius: 6, fontSize: 12 }}>
                          <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                            <Link2 size={12} color="var(--c-primary)" />
                            <a href={t.entrega_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-primary)', wordBreak: 'break-all', flex: 1 }}>
                              {t.entrega_url.length > 60 ? t.entrega_url.slice(0, 57) + '...' : t.entrega_url}
                            </a>
                          </div>
                          {t.entrega_observacao && <div className="text-xs muted" style={{ marginTop: 4 }}>{t.entrega_observacao}</div>}
                        </div>
                      )}
                    </div>
                    {podeEditar && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <select value={t.responsavel_id || ''} onChange={(e) => mudarResponsavel(t, e.target.value)} className="select" style={{ fontSize: 11, padding: '4px 6px', minHeight: 28 }}>
                          <option value="">—</option>
                          {responsaveis.map((u) => <option key={u.id} value={u.id}>{u.nome?.split(' ')[0]}</option>)}
                        </select>
                        <select value={t.status} onChange={(e) => mudarStatus(t, e.target.value)} className="select" style={{ fontSize: 11, padding: '4px 6px', minHeight: 28 }}>
                          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => remover(t)}><Trash2 size={12} /></button>
                      </div>
                    )}
                  </div>
                  {/* Form inline pedindo o link de entrega antes de concluir */}
                  {emConclusao && (
                    <ConcluirForm
                      tarefa={t}
                      onCancelar={() => setConcluindoId(null)}
                      onConcluir={(url, obs) => concluirComEntrega(t, url, obs)}
                    />
                  )}
                  {erroId?.id === t.id && (
                    <div className="text-xs" style={{ color: 'var(--c-danger)', marginTop: 6 }}>{erroId.msg}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function AddForm({ demandaId, responsaveis, onCancel, onCreated }) {
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [responsavelId, setResponsavelId] = useState('');
  const [prazo, setPrazo] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!titulo.trim()) return;
    setSaving(true);
    try {
      await api.post(`/demandas/${demandaId}/tarefas`, {
        titulo,
        descricao: descricao || null,
        responsavel_id: responsavelId ? parseInt(responsavelId, 10) : null,
        prazo: prazo || null,
      });
      onCreated?.();
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="stack" style={{ gap: 8, marginBottom: 12, padding: 12, background: 'var(--c-surface-2)', borderRadius: 10 }}>
      <input className="input" required placeholder="Título da tarefa (ex: Criar arte do mutirão)" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
      <textarea className="textarea" rows={2} placeholder="Descrição (opcional)" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
      <div className="row-wrap" style={{ gap: 8 }}>
        <select className="select" value={responsavelId} onChange={(e) => setResponsavelId(e.target.value)} style={{ flex: 1, minWidth: 150 }}>
          <option value="">Responsável</option>
          {responsaveis.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>
        <input type="date" className="input" value={prazo} onChange={(e) => setPrazo(e.target.value)} style={{ flex: 1, minWidth: 150 }} />
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? '...' : 'Criar'}</button>
      </div>
    </form>
  );
}

// Form inline pra registrar a entrega antes de marcar a tarefa como concluída.
// Aceita link de drive/youtube/figma/dropbox/etc. + observação opcional.
function ConcluirForm({ tarefa, onCancelar, onConcluir }) {
  const [url, setUrl] = useState(tarefa.entrega_url || '');
  const [obs, setObs] = useState(tarefa.entrega_observacao || '');
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErro(null);
    if (!url.trim()) {
      setErro('Cole o link da entrega pra poder concluir.');
      return;
    }
    setSaving(true);
    try {
      await onConcluir(url.trim(), obs.trim() || null);
    } catch (e) {
      setErro(e.data?.error || e.message);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} style={{ marginTop: 10, padding: 12, background: 'var(--c-primary-soft)', borderRadius: 8, border: '1px solid var(--c-primary)' }}>
      <div className="text-xs strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--c-primary)', marginBottom: 8 }}>
        Pra concluir, registre a entrega
      </div>
      <div className="field" style={{ marginBottom: 8 }}>
        <label className="text-xs">Link do material *</label>
        <input
          className="input"
          type="url"
          autoFocus
          required
          placeholder="https://drive.google.com/... · youtube · figma · dropbox"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>
      <div className="field">
        <label className="text-xs">Observação (opcional)</label>
        <textarea
          className="textarea"
          rows={2}
          placeholder="Algo que a equipe precisa saber sobre a entrega"
          value={obs}
          onChange={(e) => setObs(e.target.value)}
        />
      </div>
      {erro && <div className="text-xs" style={{ color: 'var(--c-danger)', marginBottom: 6 }}>{erro}</div>}
      <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancelar} disabled={saving}>
          <X size={12} /> Cancelar
        </button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
          <Check size={12} /> {saving ? 'Concluindo...' : 'Concluir tarefa'}
        </button>
      </div>
    </form>
  );
}
